/**
 * Whether a note is on its way, so the bell knows to look for it (D-869).
 *
 * **The feature does not work without this, and that is not obvious.** Telling
 * a class's families happens after the response now, and the screen says *"You
 * will get a note when they have all been told"* — but the bell's query has
 * `staleTime: 60_000`, no interval, and `refetchOnWindowFocus` is off globally.
 * Its own comment says so: *"Nothing else triggers one: this lives in the header
 * and never unmounts … so a tab left open through a class night would still read
 * zero at half past eight."* So the row would land in the database and the badge
 * beside her would not move. **A promise the open screen does not keep is worse
 * than the slow spinner it replaced**, which at least told her when the send was
 * done. Found by an audit.
 *
 * **A module variable rather than a context**, because the two sides are a
 * mutation's `onSuccess` and a header component that never unmounts, and there
 * is one browser tab. A provider threaded between them would be ceremony around
 * a boolean.
 *
 * **One home for it** (A-17): cancelling a class and declaring a sub-team not
 * called both hand off the same way, so they both say so the same way.
 */

/**
 * When the last-expected note stops being expected.
 *
 * Epoch milliseconds, and zero means nothing is owed. Deliberately a *deadline*
 * rather than a count: a count has to be decremented by whoever notices the
 * note, and nothing reliably notices.
 */
let expectedUntil = 0

/**
 * How long to keep looking.
 *
 * `AcsEmailSender` paces at `60 / Email:SendsPerMinute`, which is 80 a minute in
 * production and **10 on QA** — so two dozen households is about 18 seconds
 * there and 144 here. Four minutes covers QA with room, and the cost of being
 * generous is a handful of polls nobody notices; the cost of being mean is the
 * note never arriving on screen, which is the whole bug.
 */
const Window = 4 * 60 * 1000

/** How often to look while one is owed. */
export const NoteInterval = 10_000

/**
 * Say that a fan-out has been handed off and a note will follow.
 *
 * Extends rather than replaces, so calling off three classes in a row keeps
 * looking until the last of them could plausibly have finished.
 */
export function expectANote(now: number = Date.now()): void {
  expectedUntil = Math.max(expectedUntil, now + Window)
}

/** Whether the bell should keep asking. */
export function noteExpected(now: number = Date.now()): boolean {
  return now < expectedUntil
}

/**
 * Forget what is owed.
 *
 * For tests, which share a module instance across cases — a leftover deadline
 * would make an unrelated test poll.
 */
export function forgetNotes(): void {
  expectedUntil = 0
}
